/**
 * Types for the `/api/tracker/*` feature.
 *
 * All API shapes are generated from the Rust OpenAPI schemas (registered in
 * the ApiDoc aggregator in `crates/api/src/main.rs`) and surfaced via
 * `@/types/api`. This file re-exports them under the names the feature uses,
 * plus a couple of client-only default constants.
 *
 * No hand-written API shapes — to change a type, edit the Rust struct and run
 * `xtask dev gen-types`.
 */

export type {
  BoardColumnResponse,
  ApplyPart,
  BoardRefResponse,
  BoardScaffoldResponse,
  ClearCacheResponse,
  CreateDatasetRequest,
  CreateSourceRequest,
  DailyThroughput,
  DatasetPreviewRequest,
  DatasetPreviewResponse,
  DatasetResponse,
  FeatureLinkConfig,
  ImportWarning,
  IngestionResponse,
  IngestionResult,
  IngestionStatus,
  LatestIngestionResponse,
  LinkTypeResponse,
  QueryPreviewItemResponse,
  QueryPreviewResponse,
  SimulationTeamTrackerState,
  SourceResponse,
  StoredAdapterConfig,
  TeamRefreshPart,
  TeamRefreshResponse,
  TeamRefreshStatus,
  TeamThroughputResponse,
  ThroughputHistory,
  ThroughputRangeBody,
  ThroughputSettingsBody,
  TrackerAuthMethod,
  TrackerCapabilities,
  TrackerFieldResponse,
  TrackerFieldType,
  TrackerKind,
  UpdateDatasetRequest,
  UpdateSourceRequest,
} from '@/types/api';

import type { TrackerFieldResponse } from '@/types/api';

/** Default workflow JSON for new datasets — simple to-do/in-progress/done.
 *
 * `columns` is an ordered array (not an object) because column order is
 * load-bearing for the workflow engine — JSON object key order is not
 * preserved through `serde_json::Value` or PG `jsonb`, but array order
 * survives both. Each entry is `{name, statuses}`. */
export const DEFAULT_WORKFLOW = {
  columns: [
    { name: 'To Do', statuses: ['To Do', 'Backlog', 'Open'] },
    { name: 'In Progress', statuses: ['In Progress'] },
    { name: 'Done', statuses: ['Done', 'Closed'] },
  ],
} as const;

/**
 * What a NEW dataset counts as delivered: the last column of
 * {@link DEFAULT_WORKFLOW}, so the two defaults agree by construction.
 *
 * Not `DEFAULT_ADAPTER_CONFIG.departure_column`. That one mirrors the server's
 * serde default (`done`) and is right for reading back a row whose key is
 * absent — but the departure column is an exact match against the workflow's
 * column names, so against this workflow's `Done` it is a miss, and a dataset
 * saved with both defaults untouched reported `departure_column_not_found`
 * on its first sync and counted nothing as done.
 */
export const DEFAULT_DEPARTURE_COLUMN: string =
  DEFAULT_WORKFLOW.columns[DEFAULT_WORKFLOW.columns.length - 1]?.name ?? '';

/** One field a tracker source reports as available (for the field pickers). */
export type TrackerField = TrackerFieldResponse;

/** Default adapter config matching the backend's `StoredAdapterConfig` defaults. */
export const DEFAULT_ADAPTER_CONFIG = {
  parent_field: 'parent',
  due_date_field: 'due_date',
  dependency_link_type: 'Blocks',
  departure_column: 'done',
} as const;
